import { Card, cardLogoLockups } from '@/libraries/design/components/card/src/card';
import { WclImageAspectRatio } from '@/libraries/design/components/image/src/image';
import { RichTextComponent } from '@/libraries/design/utils';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';

export interface UniformCardProps {
	alignment?: 'start' | 'center';
	orientation?: 'vertical' | 'horizontal';
	columns?: 10 | 12;
	background?: 'light' | 'dark';
	eyebrow?: string;
	logo?: keyof typeof cardLogoLockups;
	title?: string;
	titleAs?: 'h2' | 'h3' | 'h4' | 'h5' | 'p';
	body?: string;
	richText?: string;
	linkText?: string;
	linkUrl?: string;
	imageUrl?: string;
	imageAlt?: string;
	imageAspectRatio?: WclImageAspectRatio;
	iconUrl?: string;
	iconSize?: 'x-small' | 'small' | 'medium' | 'large';
	iconPaddingIncluded?: boolean;
	spotIllustrationUrl?: string;
	spotIllustrationAlt?: string;
	spotIllustrationHeight?: string;
	videoSrc?: string;
	dataLabel?: string;
	dataLabelEnglish?: string;
}

const getBodyText = (props: UniformCardProps) => {
	if (!props.body && !props.richText) {
		return undefined;
	}

	if (props.richText) {
		return <RichTextComponent text={props.richText} />;
	}

	return props.body;
};

const UniformCard = (props: UniformCardProps) => {
	const localizedHref = useLocalizedHref(props.linkUrl?.toString() ?? '');

	return (
		<Card
			align={props.alignment}
			orientation={props.orientation}
			columns={props.columns}
			background={props.background}
			eyebrow={props.eyebrow ? props.eyebrow : undefined}
			logo={props.logo}
			title={props.title ? props.title : undefined}
			titleAs={props.titleAs}
			body={getBodyText(props)}
			callToAction={
				props.videoSrc && props.linkText
					? {
							text: props.linkText,
							href: props.videoSrc,
							dataEventProps: {
								'data-product-key': 'uniform',
								'data-event-component': 'linkButton',
								'data-event-container': 'videoCard',
								'data-event': 'clicked',
								'data-label': props.dataLabel,
								'data-label-english': props.dataLabelEnglish,
							},
						}
					: props.linkUrl && props.linkText
						? {
								text: props.linkText,
								href: localizedHref,
								dataEventProps: {
									'data-product-key': 'uniform',
									'data-event-component': 'linkButton',
									'data-event-container': 'card',
									'data-event': 'clicked',
									'data-label': props.dataLabel,
									'data-label-english': props.dataLabelEnglish,
								},
							}
						: undefined
			}
			image={
				props.imageUrl
					? {
							src: props.imageUrl,
							alt: props.imageAlt ?? '',
						}
					: undefined
			}
			imageAspectRatio={props.imageAspectRatio}
			icon={
				props.iconUrl
					? {
							linkedIcon: {
								url: props.iconUrl,
								size: props.iconSize,
								paddingIncluded: props.iconPaddingIncluded,
							},
						}
					: undefined
			}
			spotIllustration={
				props.spotIllustrationUrl
					? {
							src: props.spotIllustrationUrl,
							alt: props.spotIllustrationAlt,
							height: props.spotIllustrationHeight,
						}
					: undefined
			}
			videoSrc={props.videoSrc}
		/>
	);
};

export default UniformCard;
